import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import Input from '../common/Input';
import Button from '../common/Button';
import api from '../../services/api';

const CustomerSelect = ({ isOpen, onClose, onSelect }) => {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [isAdding, setIsAdding] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    first_name: '',
    last_name: '',
    phone: ''
  });
  const [addLoading, setAddLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadCustomers();
      setIsAdding(false);
      setNewCustomer({ first_name: '', last_name: '', phone: '' });
      setError(null);
    }
  }, [isOpen]);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/customers.php');
      setCustomers(response.data.customers || []);
    } catch (error) {
      console.error('Failed to load customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(customer => 
    customer.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone?.includes(searchTerm)
  );

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    setError(null);
    if (!newCustomer.first_name || !newCustomer.last_name || !newCustomer.phone) {
      setError("First Name, Last Name, and Phone are required.");
      return;
    }
    
    setAddLoading(true);
    try {
      const response = await api.post('/customers.php', newCustomer);
      if (response.data.success) {
        // Automatically select the new customer
        const newlyCreated = {
          id: response.data.customer_id,
          first_name: newCustomer.first_name,
          last_name: newCustomer.last_name,
          phone: newCustomer.phone,
          customer_code: response.data.customer_code,
          credit_limit: 0,
          current_balance: 0
        };
        onSelect(newlyCreated);
        onClose();
      } else {
        setError(response.data.error || "Failed to add customer");
      }
    } catch (err) {
      setError(err.response?.data?.error || "An error occurred");
    } finally {
      setAddLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isAdding ? "Add New Customer" : "Select Customer"} size="lg">
      <div className="customer-select">
        {isAdding ? (
          <form className="add-customer-form" onSubmit={handleAddCustomer}>
            {error && <div className="error-message" style={{ color: 'red', marginBottom: '10px' }}>{error}</div>}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
              <Input
                label="First Name"
                value={newCustomer.first_name}
                onChange={(e) => setNewCustomer({...newCustomer, first_name: e.target.value})}
                required
              />
              <Input
                label="Last Name"
                value={newCustomer.last_name}
                onChange={(e) => setNewCustomer({...newCustomer, last_name: e.target.value})}
                required
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <Input
                label="Phone Number"
                value={newCustomer.phone}
                onChange={(e) => setNewCustomer({...newCustomer, phone: e.target.value})}
                required
              />
            </div>
            <div className="form-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <Button type="button" variant="secondary" onClick={() => setIsAdding(false)} disabled={addLoading}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={addLoading}>
                {addLoading ? 'Saving...' : 'Save Customer'}
              </Button>
            </div>
          </form>
        ) : (
          <>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
              <div style={{ flex: 1 }}>
                <Input
                  type="text"
                  placeholder="Search customers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  icon="🔍"
                />
              </div>
              <Button variant="primary" onClick={() => setIsAdding(true)}>
                + New Customer
              </Button>
            </div>

            <div className="quick-actions">
              <Button variant="secondary" onClick={() => {
                onSelect(null);
                onClose();
              }}>
                Continue as Guest
              </Button>
            </div>

            <div className="customers-list">
              {loading ? (
                <p>Loading...</p>
              ) : filteredCustomers.length === 0 ? (
                <p className="no-results">No customers found</p>
              ) : (
                filteredCustomers.map(customer => (
                  <div
                    key={customer.id}
                    className="customer-item"
                    onClick={() => {
                      onSelect(customer);
                      onClose();
                    }}
                  >
                    <div className="customer-avatar">
                      {customer.first_name?.[0]}{customer.last_name?.[0]}
                    </div>
                    <div className="customer-info">
                      <div className="customer-name">
                        {customer.first_name} {customer.last_name}
                      </div>
                      <div className="customer-phone">{customer.phone}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default CustomerSelect;